import { useRecoilValue, useSetRecoilState } from 'recoil';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import { mpsActivationInfoAtom } from '../../recoil/atoms/mpsActivationInfoAtom';
import { Grid, GridItem } from '@flos/react-ui';
import CheckoutSidebar from '../CheckoutPage/components/CheckoutSidebar';
import { fetchedPaymentInformationAtom } from '../../recoil/atoms/fetchedPaymentInformationAtom';
import MPSVariantBox from './components/MPSVariantBox';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import useActivateMPS from '../../recoil/hooks/useActivateMPS';
import { mpsStatusAtom } from '../../recoil/atoms/mpsStatusAtom';
import { useCallback, useEffect } from 'react';
import { refreshCoverablesSelectorAtom } from '../../recoil/atoms/refreshCoverablesSelectorAtom';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import ErrorModal from '../../shared_components/ErrorModal/ErrorModal';
import { hideInfoInMpsSelector } from '../../recoil/selectors/hideInfoInMpsSelector';

const MPSActivationPage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const { policeNummer, isConfirmedToPay } = useRecoilValue(mpsActivationInfoAtom);
    const { status } = useRecoilValue(mpsStatusAtom);
    const { paymentMethod } = useRecoilValue(fetchedPaymentInformationAtom);
    const isPaymentMethodMPS = paymentMethod === 'MPS_Top';
    const { activateMps, showErrorMessage } = useActivateMPS();
    const redirectUrl = `${window.location.href}&confirmedToPay`;
    const refreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);
    const { isCancelled } = useRecoilValue(jobAtom);
    const shouldHideInfoInMps = useRecoilValue(hideInfoInMpsSelector);

    const handleConfirmToPay = (phoneNumber: string | undefined): void => {
        const payerPhoneNumber = phoneNumber ?? '';
        // Use the redirectUrl ='https://www.topdanmark.dk/' when running in Local environment
        // const redirectUrl = 'https://www.topdanmark.dk/';
        activateMps(policeNummer, payerPhoneNumber, redirectUrl);
    };

    const navigateToPageInMPS = useCallback((): void => {
        refreshCoverablesSelector({});
        if (isConfirmedToPay) {
            wizardGotoStep('MPSAgreementStatusPage');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        navigateToPageInMPS();
    }, [navigateToPageInMPS]);

    const getMPSVariantOnStatus = (): React.JSX.Element => {
        if (!isPaymentMethodMPS) {
            return (
                <MPSVariantBox
                    variant={'OtherPayment'}
                    otherPaymentMethod={paymentMethod}
                ></MPSVariantBox>
            );
        }
        if (isCancelled) {
            return <MPSVariantBox variant={'CancelledPolicy'}></MPSVariantBox>;
        }
        if (status === 'Active') {
            return <MPSVariantBox variant={'Active'}></MPSVariantBox>;
        }
        return (
            <MPSVariantBox variant={'New'} handleConfirmToPay={handleConfirmToPay}></MPSVariantBox>
        );
    };

    return (
        <Grid className={'u-shadow-none u-spacing-inset-m--squish-max'}>
            <GridItem colStart={2} span={6}>
                {getMPSVariantOnStatus()}
            </GridItem>
            <GridItem colStart={9} span={4}>
                {!shouldHideInfoInMps && <CheckoutSidebar />}
            </GridItem>
            {showErrorMessage && <ErrorModal />}
        </Grid>
    );
};

export default MPSActivationPage;
